import React, { useState } from "react";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { getErrorMessage } from "utils/errorHelpers";
import { formatFullName } from "utils/formatters/NameFormatters";
import type { BillingAccountDetailDto, ContactDto, QuoteTaxRecalculationDto } from "client/api-v2";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { PaperLayout } from "components/PaperLayout/PaperLayout";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { AccountIssueChip } from "../components/AccountIssueChip";
import { BillingAccountDialog } from "../components/BillingAccountDialog";
import { ContactDialog } from "../components/ContactDialog";
import { PushToZuoraDialog } from "../components/PushToZuoraDialog";
import { ResellerChip } from "../components/ResellerChip";
import { hasFailedTaxRecalculation, taxRecalculationSummary } from "../liveQuoteTax";
import { getBillingAccountIntegrationAttempts, removeBillingAccountContact } from "../services/billingAccountService";
import { BillingAccountSummary } from "./BillingAccountSummary";
import { ContactsList } from "./ContactsList";
import { IntegrationAttemptsList } from "./IntegrationAttemptsList";

function withTaxRecalculations(message: string, recalculations: readonly QuoteTaxRecalculationDto[]): string {
  const summary = taxRecalculationSummary(recalculations);
  return summary ? `${message}. ${summary}` : message;
}

function severityFor(recalculations: readonly QuoteTaxRecalculationDto[]): AlertSeverity {
  return hasFailedTaxRecalculation(recalculations) ? AlertSeverity.Warning : AlertSeverity.Success;
}

export function BillingAccountDetails() {
  const { sendNewAlert } = useAlerts();
  const { entity: billingAccount, refresh } = useEntity<BillingAccountDetailDto>();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  // The contact is the one being edited, and absent when adding. It outlives `open` so the dialog
  // keeps its title and fields through the closing transition rather than reverting to the add form.
  const [contactDialog, setContactDialog] = useState<{ open: boolean; contact?: ContactDto }>({ open: false });
  const [isPushToZuoraDialogOpen, setIsPushToZuoraDialogOpen] = useState(false);
  const [contactPendingRemoval, setContactPendingRemoval] = useState<ContactDto | undefined>(undefined);
  // The hook cannot be skipped and the account is not resolved on the first render, so an absent
  // code answers with nothing rather than asking the API about an account that has no code.
  const { data: integrationAttempts, refresh: refreshIntegrationAttempts } = useAsyncRequest(
    getBillingAccountIntegrationAttempts,
    billingAccount?.code
  );

  if (!billingAccount) {
    return null;
  }

  const contactsIssueChip = billingAccount.hasContactsIssue ? <AccountIssueChip issue="contacts" /> : undefined;
  const resellerChip = billingAccount.isReseller ? <ResellerChip /> : undefined;
  const accountName = billingAccount.commonName ?? billingAccount.legalName;

  const removeContact = async () => {
    const contact = contactPendingRemoval;
    if (!contact) {
      return;
    }

    try {
      await removeBillingAccountContact(billingAccount.code, contact.id);
      sendNewAlert("Contact removed", AlertSeverity.Success);
    } catch (error) {
      sendNewAlert(getErrorMessage(error), AlertSeverity.Error);
    } finally {
      // The dialog stays dismissable while the request is in flight, so by now the user may have
      // opened it again for someone else — close only the prompt this removal started from.
      setContactPendingRemoval((pending) => (pending === contact ? undefined : pending));
      // Refetch after a failure too — the likeliest one is the contact already being gone,
      // and the grid should show whatever the server now holds either way.
      await refresh?.();
    }
  };

  return (
    <Stack spacing={3}>
      <PaperLayout maxWidth={false}>
        <DetailsPageLayout
          pageName={billingAccount.legalName}
          titleBadge={resellerChip}
          actions={[
            <PolicyAuthorized key="edit" policies={RbacPolicies.BillingAccounts.ReadWrite}>
              <Button
                id="edit-billing-account-button"
                color="secondary"
                variant="outlined"
                onClick={() => setIsEditDialogOpen(true)}
              >
                Edit
              </Button>
            </PolicyAuthorized>,
          ]}
        >
          <BillingAccountSummary
            billingAccount={billingAccount}
            onPushToBillingSystem={() => setIsPushToZuoraDialogOpen(true)}
          />
        </DetailsPageLayout>
      </PaperLayout>
      <PaperLayout maxWidth={false}>
        <DetailsPageLayout
          pageName="Contacts"
          titleBadge={contactsIssueChip}
          actions={[
            <PolicyAuthorized key="add-contact" policies={RbacPolicies.BillingAccounts.ReadWrite}>
              <Button
                id="add-billing-account-contact-button"
                color="secondary"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setContactDialog({ open: true })}
              >
                Add Contact
              </Button>
            </PolicyAuthorized>,
          ]}
        />
        <ContactsList
          contacts={billingAccount.contacts}
          onEdit={(contact) => setContactDialog({ open: true, contact })}
          onRemove={setContactPendingRemoval}
        />
      </PaperLayout>
      <PaperLayout maxWidth={false}>
        <DetailsPageLayout pageName="External system activity" />
        <IntegrationAttemptsList attempts={integrationAttempts ?? []} />
      </PaperLayout>
      <BillingAccountDialog
        open={isEditDialogOpen}
        billingAccount={billingAccount}
        liveOrderCount={billingAccount.liveOrderCount ?? 0}
        onCancel={() => setIsEditDialogOpen(false)}
        onSaved={(_, taxRecalculations = []) => {
          setIsEditDialogOpen(false);
          sendNewAlert(
            withTaxRecalculations("Billing account updated", taxRecalculations),
            severityFor(taxRecalculations)
          );
          void refresh?.();
        }}
      />
      <ContactDialog
        open={contactDialog.open}
        billingAccountCode={billingAccount.code}
        contact={contactDialog.contact}
        onCancel={() => setContactDialog((dialog) => ({ ...dialog, open: false }))}
        onSaved={(contact) => {
          const action = contactDialog.contact ? "updated" : "added";
          setContactDialog((dialog) => ({ ...dialog, open: false }));
          sendNewAlert(`Contact ${contact.email} ${action}`, AlertSeverity.Success);
          void refresh?.();
        }}
      />
      <PushToZuoraDialog
        open={isPushToZuoraDialogOpen}
        billingAccount={billingAccount}
        onCancel={() => setIsPushToZuoraDialogOpen(false)}
        onFailed={() => {
          void refresh?.();
          refreshIntegrationAttempts();
        }}
        onPushed={(result) => {
          setIsPushToZuoraDialogOpen(false);
          sendNewAlert(
            withTaxRecalculations(
              `Pushed to Zuora — write-through verified against ${result.zuoraAccountNumber}`,
              result.taxRecalculations ?? []
            ),
            severityFor(result.taxRecalculations ?? [])
          );
          void refresh?.();
          refreshIntegrationAttempts();
        }}
      />
      <ActionDialog
        title="Remove contact"
        description={
          contactPendingRemoval && (
            <>
              Remove <strong>{formatFullName(contactPendingRemoval)}</strong>{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                &lt;{contactPendingRemoval.email}&gt;
              </Box>{" "}
              from {accountName}?
            </>
          )
        }
        show={!!contactPendingRemoval}
        confirmText="Remove"
        onConfirm={removeContact}
        onCancel={() => setContactPendingRemoval(undefined)}
      />
    </Stack>
  );
}
